/**
 * Inline markdown styling for the rich text composer.
 *
 * Ported from t3code's composer rich-text layer (ticket 3526, upstream pin
 * e3e7cc3f, MIT license retained in LICENSE), with the run handling extracted
 * into helpers. The composer's stored prompt stays plain markdown (`**bold**`)
 * while the Tiptap surface renders styled text; these helpers translate
 * between the two: parsing markdown into marked spans for the document,
 * serializing marked spans back to markdown in the document serializer.
 *
 * Deliberately small: bold, italic, strikethrough, and inline code only.
 * Unmatched markers stay literal text so nothing the user typed is ever lost.
 */

export type RichTextMark = "bold" | "italic" | "strike" | "code";

interface RichTextSpan {
  text: string;
  marks: RichTextMark[];
}

export const RICH_TEXT_DELIMITERS: Record<RichTextMark, string> = {
  bold: "**",
  italic: "*",
  strike: "~~",
  code: "`",
};

interface StackFrame {
  delimiter: string;
  mark: RichTextMark;
  spans: RichTextSpan[];
}

function pushSpan(spans: RichTextSpan[], text: string, marks: RichTextMark[]): void {
  if (!text) return;
  const last = spans[spans.length - 1];
  if (
    last &&
    last.marks.length === marks.length &&
    last.marks.every((mark, index) => mark === marks[index])
  ) {
    last.text += text;
  } else {
    spans.push({ text, marks });
  }
}

/** A run of one emphasis character, with whether it may open or close a span. */
interface MarkerRun {
  char: string;
  length: number;
  canOpen: boolean;
  canClose: boolean;
}

// fallow-ignore-next-line complexity -- ported upstream t3code algorithm (ticket 3526); behavior pinned by the ported spec
function markerRunAt(text: string, index: number): MarkerRun | null {
  const char = text[index]!;
  if (char !== "*" && char !== "_" && char !== "~") return null;
  let end = index;
  while (text[end] === char) end += 1;
  const before = text[index - 1] ?? "";
  const after = text[end] ?? "";
  const canClose = before !== "" && !/\s/.test(before) && (char !== "_" || !/\w/.test(after));
  const canOpen = after !== "" && !/\s/.test(after) && (char !== "_" || !/\w/.test(before));
  return { char, length: end - index, canOpen, canClose };
}

/** Close the top frame into the spans one level out, mirroring its opener. */
function closeFrame(current: () => RichTextSpan[], stack: StackFrame[], index: number): number {
  const top = stack.at(-1)!;
  stack.pop();
  for (const span of top.spans) pushSpan(current(), span.text, [top.mark, ...span.marks]);
  return index + top.delimiter.length;
}

/** A frame still open at end of input never opened: its markers stay literal. */
function flushUnclosedFrames(current: () => RichTextSpan[], stack: StackFrame[]): void {
  while (stack.length > 0) {
    const frame = stack.pop()!;
    pushSpan(current(), frame.delimiter, []);
    for (const span of frame.spans) pushSpan(current(), span.text, span.marks);
  }
}

// fallow-ignore-next-line complexity -- ported upstream t3code algorithm (ticket 3526); behavior pinned by the ported spec
function consumeMarkerRun(
  current: () => RichTextSpan[],
  stack: StackFrame[],
  text: string,
  run: MarkerRun,
  index: number,
): number {
  const end = index + run.length;
  while (index < end) {
    const top = stack.at(-1);
    // Inside italic, a double marker opens bold before closing the single
    // marker. Once bold is active, closing runs unwind both styles.
    const opensNestedBold =
      top?.mark === "italic" &&
      end - index === 2 &&
      run.canOpen &&
      (run.char === "*" || run.char === "_") &&
      !stack.some((frame) => frame.mark === "bold");
    if (
      !opensNestedBold &&
      run.canClose &&
      top &&
      text.startsWith(top.delimiter, index) &&
      index + top.delimiter.length <= end
    ) {
      index = closeFrame(current, stack, index);
    } else if (run.canOpen && (run.char !== "~" || end - index >= 2)) {
      const length = run.char === "~" || end - index >= 2 ? 2 : 1;
      const mark = run.char === "~" ? "strike" : length === 2 ? "bold" : "italic";
      // A mark cannot nest inside itself, including alternate delimiters.
      // This bounds the stack to the supported styles for arbitrary input.
      if (stack.some((frame) => frame.mark === mark)) {
        pushSpan(current(), text.slice(index, end), []);
        return end;
      }
      stack.push({ delimiter: run.char.repeat(length), mark, spans: [] });
      index += length;
    } else {
      pushSpan(current(), text.slice(index, end), []);
      return end;
    }
  }
  return index;
}

/** Parse the supported inline styles, leaving unmatched and escaped markers literal. */
export function parseInlineMarkdown(text: string): RichTextSpan[] {
  const root: RichTextSpan[] = [];
  const stack: StackFrame[] = [];
  const current = () => stack.at(-1)?.spans ?? root;
  let index = 0;
  while (index < text.length) {
    const char = text[index]!;
    if (char === "\\") {
      pushSpan(current(), text.slice(index, index + 2), []);
      index += 2;
      continue;
    }
    if (char === "`") {
      // Multi-backtick code stays literal; a single-backtick span owns its
      // contents.
      const run = text.slice(index).match(/^`+/)![0];
      const close = text.indexOf(run, index + run.length);
      if (
        run.length === 1 &&
        close > index + 1 &&
        !text.slice(index, close).includes("\n") &&
        text[close + 1] !== "`"
      ) {
        pushSpan(current(), text.slice(index + 1, close), ["code"]);
        index = close + 1;
      } else {
        pushSpan(current(), run, []);
        index += run.length;
      }
      continue;
    }
    const run = markerRunAt(text, index);
    if (!run) {
      pushSpan(current(), char, []);
      index += 1;
      continue;
    }
    index = consumeMarkerRun(current, stack, text, run, index);
  }
  flushUnclosedFrames(current, stack);
  return root;
}
